import styles from './assign-license-rights-dialog.module.scss';
import { useGetRequestNewHook, useMakeRequestHook } from '../../requestUtils';
import { useContext, useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import { booleanHeader, Checkbox, CheckboxLabel, CreateHeaders, dateHeader, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, MenuItem, PrimaryButton, Select, SnackBarContext, textHeader } from '@frontend-workspace/react-shared-lib';
import { ILicenseWithAccesses } from '../../_models/license-with-accesses';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { addDays, addMonths } from 'date-fns';

/* eslint-disable-next-line */
export interface AssignLicenseRightsDialogProps {
  open: boolean,
  onClose: () => void,
  email: string,
  isTrial?: boolean
}

export function AssignLicenseRightsDialog(props: AssignLicenseRightsDialogProps) {
  const makeRequest = useMakeRequestHook();
  const url = props.isTrial ? `accounts/${props.email}/demoLicenseAccess` : `accounts/${props.email}/licenseAccess`;
  const { data }: { data: ILicenseWithAccesses[] } = useGetRequestNewHook(url, []);
  const [endDate, setEndDate] = useState<Date | null>(addMonths(new Date(), 1));
  const [actionName, setActionName] = useState<string>('');
  const [addedAsUser, setAddedAsUser] = useState<number[]>([]);
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [addedAsCoordinator, setAddedAsCoordinator] = useState<number[]>([]);
  const [removedAsUser, setRemovedAsUser] = useState<number[]>([]);
  const [removedAsCoordinator, setRemovedAsCoordinator] = useState<number[]>([]);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();


  const handleActionChange = (event: any) => setActionName(event.target.value);
  const handleSendEmail = (event: React.ChangeEvent<Element>, checked: boolean) => setSendEmail(checked);

  const onAccessChange = (licenseNumber: number, isCoordinator: boolean, newValue: boolean) => {
    const initialLicenseAccess = data.find(l => l.licenseNumber === licenseNumber);
    if (!initialLicenseAccess) return;
    if (isCoordinator) {
      if (!newValue && initialLicenseAccess.isCoordinator) setRemovedAsCoordinator(removedAsCoordinator.concat(licenseNumber));
      if (newValue && !initialLicenseAccess.isCoordinator) setAddedAsCoordinator(addedAsCoordinator.concat(licenseNumber));
      if (newValue && initialLicenseAccess.isCoordinator) setRemovedAsCoordinator(removedAsCoordinator.filter(n => n !== licenseNumber));
      if (!newValue && !initialLicenseAccess.isCoordinator) setAddedAsCoordinator(addedAsCoordinator.filter(n => n !== licenseNumber));
    } else {
      if (!newValue && initialLicenseAccess.hasAccess) setRemovedAsUser(removedAsUser.concat(licenseNumber));
      if (newValue && !initialLicenseAccess.hasAccess) setAddedAsUser(addedAsUser.concat(licenseNumber));
      if (newValue && initialLicenseAccess.hasAccess) setRemovedAsUser(removedAsUser.filter(n => n !== licenseNumber));
      if (!newValue && !initialLicenseAccess.hasAccess) setAddedAsUser(addedAsUser.filter(n => n !== licenseNumber));
    }
  };

  const changeAccessRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      onAccessChange(params.data.licenseNumber, false, checked);
    };
    const isChecked = () => {
      if (removedAsUser.includes(params.data.licenseNumber)) return false;
      if (addedAsUser.includes(params.data.licenseNumber)) return true;
      return params.value;
    };
    return <Checkbox
      checked={isChecked()}
      onChange={handleOnClick}
    />
  };

  const changeCoordinatorRenderer = (params: ValueFormatterParams) => {
    const handleOnClick = (event: React.ChangeEvent<Element>, checked: boolean) => {
      onAccessChange(params.data.licenseNumber, true, checked);
    };
    const isChecked = () => {
      if (removedAsCoordinator.includes(params.data.licenseNumber)) return false;
      if (addedAsCoordinator.includes(params.data.licenseNumber)) return true;
      return params.value;
    };
    return <Checkbox
      checked={isChecked()}
      onChange={handleOnClick}
    />
  };

  const handleGiveAllAccess = () => {
    const licensesWithoutAccess = data.filter(l => !l.hasAccess);
    const licenseNumbers = licensesWithoutAccess.map(l => l.licenseNumber);
    setRemovedAsUser([]);
    setAddedAsUser(addedAsUser.concat(licenseNumbers));
  };

  const handleRemoveAllAccess = () => {
    const licensesWithAccess = data.filter(l => l.hasAccess);
    const licenseNumbers = licensesWithAccess.map(l => l.licenseNumber);
    setAddedAsUser([]);
    setRemovedAsUser(removedAsUser.concat(licenseNumbers));
  };

  const handleAllCoordinator = () => {
    const licensesWithAccess = data.filter(l => !l.isCoordinator);
    const licenseNumbers = licensesWithAccess.map(l => l.licenseNumber);
    setRemovedAsCoordinator([]);
    setAddedAsCoordinator(addedAsCoordinator.concat(licenseNumbers));
  };

  const handleRemoveCoordinator = () => {
    const licensesWithAccess = data.filter(l => l.isCoordinator);
    const licenseNumbers = licensesWithAccess.map(l => l.licenseNumber);
    setAddedAsCoordinator([]);
    setRemovedAsCoordinator(removedAsCoordinator.concat(licenseNumbers));
  };
  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
  }

  const handleSaveChanges = () => {
    const givenAccesses = addedAsUser.map(licenseNumber => { return { licenseNumber, giveAccess: true, isCoordinator: false, email: props.email } });
    const removedAccess = removedAsUser.map(licenseNumber => { return { licenseNumber, giveAccess: false, isCoordinator: false, email: props.email } });
    const givenCoordinator = addedAsCoordinator.map(licenseNumber => { return { licenseNumber, giveAccess: true, isCoordinator: true, email: props.email } });
    const removedCoordinator = removedAsCoordinator.map(licenseNumber => { return { licenseNumber, giveAccess: false, isCoordinator: true, email: props.email } });
    const allChanges = [...givenAccesses, ...removedAccess, ...givenCoordinator, ...removedCoordinator];
    const body = {
      sendEmail,
      accessChanges: allChanges,
      endDate: props.isTrial && endDate?.toISOString()
    }
    const url = props.isTrial ? `licenses/action/changeTrialLicenseAccess` : `v2/licenses/action/changeLicenseAccess`;
    makeRequest(url, "PUT", body)
      .then(result => {
        setSnackbarMessage({ content: t('assignLicenseRight.changesLicenseAccess'), type: "info" });
        props.onClose();
      }).catch(() => setSnackbarMessage({ content: t('assignLicenseRight.couldNotChangeLicenseRights'), type: "error" }));
  };

  const headers = CreateHeaders([
    textHeader("licenseNumber", t('standard.licenseNumber'), HeaderSizeEnum.SMALL),
    textHeader("productName", t('standard.productName'), HeaderSizeEnum.MEDIUM),
    textHeader("contractNumber", t('standard.contractNo'), HeaderSizeEnum.SMALL),
    textHeader("licenseType", t('standard.type'), HeaderSizeEnum.SMALL),
    booleanHeader("hasDomainAccess", t('standard.domainAccess')),
    textHeader("hasAccess", t('standard.access'), HeaderSizeEnum.SMALL, {
      renderer: changeAccessRenderer
    }),
    textHeader("isCoordinator", t('standard.licensingCoordinator'), HeaderSizeEnum.SMALL, {
      renderer: changeCoordinatorRenderer
    }),
    dateHeader("stopDate", t('standard.endDate'))
  ]);

  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('standard.assignLicenseRights')}
      </DialogTitle>
      <DialogContent>
        <div className="d-flex flex-row align-items-end flex-wrap">
          <div className='mb-3 mb-xxl-0 me-4'>
            <Select
              fullWidth
              label={t('standard.action')}
              name='actionName'
              value={actionName}
              onChange={handleActionChange}
              sx={{ width: 250 }}>
              <MenuItem value={0} onClick={handleGiveAllAccess}>{t('standard.giveEveryoneLicenseAccess')}</MenuItem>
              <MenuItem value={1} onClick={handleAllCoordinator}>{t('standard.makeEveryoneACoordinator')}</MenuItem>
              <MenuItem value={2} onClick={handleRemoveAllAccess}>{t('standard.removeAllLicenseAccess')}</MenuItem>
              <MenuItem value={3} onClick={handleRemoveCoordinator}>{t('standard.removeAllCoordinators')}</MenuItem>
            </Select>
          </div>
          <div className='mb-3 mb-xxl-0'>
            <CheckboxLabel
              label={t('standard.sendEmail')}
              checked={sendEmail}
              onChange={handleSendEmail}
            />
          </div>

          {props.isTrial && <div>
            <DatePicker
              onChange={handleEndDateChange}
              value={endDate}
              label={t('standard.stopDate')}
            />
          </div>}
        </div>
        <WebkuaTable
          headers={headers}
          data={data}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleSaveChanges}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default AssignLicenseRightsDialog;